import { useState } from "react";
import { accountSchemas, type accountTypes } from "@pineseed/spec";
import { EntityTable, type Column } from "../components/EntityTable";
import { EntityForm } from "../components/EntityForm";
import { Modal } from "../components/Modal";
import { useAdmin } from "../context/AdminContext";
import { useCopyToClipboard } from "@pineseed/gui/hook/useCopyToClipboard";
import { CopyableText } from "@pineseed/gui/component/CopyableText";

type Organization = accountTypes["Organization"];

export function OrganizationsPanel() {
  const { account: accountAdmin } = useAdmin();
  const { organizations, accounts } = accountAdmin;
  const { copyToClipboard, isCopied } = useCopyToClipboard();
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<Organization | null>(null);

  // CRUD handlers
  const handleCreate = async (data: Partial<Organization>) => {
    try {
      await organizations.createEntity({
        accountId: data.accountId!,
        slug: data.slug!,
        name: data.name!,
        ...(data.metadata && { metadata: data.metadata }),
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create organization:', error);
      throw error;
    }
  };

  const handleEdit = async (data: Partial<Organization>) => {
    if (!editingOrganization) return;
    try {
      await organizations.updateEntity(editingOrganization.id, data);
      setEditingOrganization(null);
    } catch (error) {
      console.error('Failed to update organization:', error);
      throw error;
    }
  };

  const handleDelete = async (organization: Organization) => {
    if (!confirm(`Delete organization "${organization.name}"? This will cascade delete all workspaces.`)) {
      return;
    }
    try {
      await organizations.deleteEntity(organization.id);
    } catch (error) {
      console.error('Failed to delete organization:', error);
    }
  };

  // Table columns
  const organizationColumns: Column<Organization>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (org) => (
        <div className="font-medium text-gray-900 dark:text-gray-100">{org.name}</div>
      ),
    },
    {
      key: 'accountId',
      label: 'Account',
      render: (org) => {
        if (!org.accountId) {
          return <span className="text-xs text-gray-400">No Account</span>;
        }
        const account = accounts.entities.find(a => a.id === org.accountId);
        return (
          <div>
            <div className="text-sm text-gray-900 dark:text-gray-100">{account?.name || 'Unknown'}</div>
            <CopyableText
              id={org.accountId}
              onCopy={copyToClipboard}
              isPulsing={isCopied(org.accountId)}
              variant="muted"
            >
              {org.accountId.slice(0, 8)}...
            </CopyableText>
          </div>
        );
      },
    },
    {
      key: 'id',
      label: 'Org ID',
      render: (org) => (
        <CopyableText
          id={org.id}
          onCopy={copyToClipboard}
          isPulsing={isCopied(org.id)}
        >
          {org.id.slice(0, 8)}...
        </CopyableText>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (org) => (
        <span className="text-xs text-gray-600 dark:text-gray-400">
          {new Date(org.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (org) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setEditingOrganization(org); }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
          >
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(org); }}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const stats = organizations.getStats();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Organizations ({stats.total})
        </h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
        >
          Create Organization
        </button>
      </div>

      {organizations.error && (
        <div className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 p-4 rounded-lg">
          {organizations.error}
        </div>
      )}

      <EntityTable
        columns={organizationColumns}
        data={organizations.entities}
        loading={organizations.loading}
      />

      {/* Create Modal */}
      <Modal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="Create Organization"
      >
        <EntityForm
          schema={accountSchemas.CreateOrganizationRequest}
          onSubmit={handleCreate}
          onCancel={() => setShowCreateForm(false)}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingOrganization}
        onClose={() => setEditingOrganization(null)}
        title="Edit Organization"
      >
        {editingOrganization && (
          <EntityForm
            schema={accountSchemas.UpdateOrganizationRequest}
            initialData={editingOrganization}
            onSubmit={handleEdit}
            onCancel={() => setEditingOrganization(null)}
          />
        )}
      </Modal>
    </div>
  );
}

